import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useApolloClient, gql, useQuery, useLazyQuery } from '@apollo/client';
import {
  Form,
  Button,
  Steps,
  Alert,
  Spin,
  Modal,
  Progress,
  Typography,

} from 'antd';
import {
  LoadingOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { CREATE_CAMPAIGN, GENERATE_PREVIEW_MESSAGES, PROCESS_CAMPAIGN } from '../../mutations/campaign';
import { SCHEDULE_CAMPAIGN } from '../../graphql/mutations/scheduleCampaign';
import { SEARCH_GROUPS, GET_GROUP } from '../../mutations/groups';
import { GET_ACCOUNT } from '../../mutations/account';
import { useAccount } from '../../contexts/AccountContext';
import dayjs from 'dayjs';

// Import step components
import BasicInfoStep from './steps/BasicInfoStep';
import SelectionStep from './steps/SelectionStep';
import MessageStep from './steps/MessageStep';
import ReviewStep from './steps/ReviewStep';
import MessagesStep from './steps/MessagesStep';
import SendStep from './steps/SendStep';
import { GET_CAMPAIGN_MESSAGES, GET_CUSTOMERS, GET_PRODUCTS } from '../../graphql/queries/campaign';

// Note: GET_CUSTOMERS aqui é usado para prefetch de dados iniciais, não para busca dinâmica
// Para busca dinâmica de clientes, o SelectionStep usa SEARCH_CUSTOMERS que é mais eficiente

const { Step } = Steps;
const { Title, Text } = Typography;

// Adicionar as consultas para buscar tags
const SEARCH_TAGS = gql`
  query SearchTags($query: String!) {
    searchTags(query: $query) {
      id
      name
      active
      fontColor
      backgroundColor
      customersCount
      customersWithWhatsappCount
    }
  }
`;

const GET_CAMPAIGN_CUSTOMERS_COUNT = gql`
  query GetCampaignCustomersCount($kind: String!, $customerIds: [ID!], $tagIds: [ID!], $dateFilters: [JSON!], $advancedFilters: [JSON!]) {
    campaignCustomersCount(kind: $kind, customerIds: $customerIds, tagIds: $tagIds, dateFilters: $dateFilters, advancedFilters: $advancedFilters)
  }
`;

const CampaignForm = ({ initialCampaignState = null, onFinish, onCancel }) => {
  const [form] = Form.useForm();
  const client = useApolloClient();
  const { account } = useAccount();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCampaignType, setSelectedCampaignType] = useState('offer');
  const [campaignProcessed, setCampaignProcessed] = useState(false);
  const [campaignMode, setSelectionMode] = useState('products'); // 'products' or 'group'
  const [customerSelectionMode, setCustomerSelectionMode] = useState('direct'); // 'direct' or 'tags'

  const [generatedMessages, setGeneratedMessages] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [error, setError] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(10);
  const [messageStatus, setMessageStatus] = useState("");
  const [totalMessagesCount, setTotalMessagesCount] = useState(0);
  const [showAiPrompt, setShowAiPrompt] = useState(false);

  // New state for products and customers
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);

  // Refs para debounce dos searches
  const groupsTimeoutRef = useRef(null);
  const tagsTimeoutRef = useRef(null);

  const [createCampaign, { loading: creatingCampaign }] = useMutation(CREATE_CAMPAIGN);
  const [processCampaign, { loading: processingCampaign }] = useMutation(PROCESS_CAMPAIGN);
  const [scheduleCampaign, { loading: schedulingCampaign }] = useMutation(SCHEDULE_CAMPAIGN);
  const [generatePreviewMessages] = useMutation(GENERATE_PREVIEW_MESSAGES);
  const [searchGroups] = useLazyQuery(SEARCH_GROUPS, {
    fetchPolicy: 'network-only'
  });
  const [searchTags] = useLazyQuery(SEARCH_TAGS, {
    fetchPolicy: 'network-only'
  });
  const [getCampaignCustomersCount] = useLazyQuery(GET_CAMPAIGN_CUSTOMERS_COUNT, {
    fetchPolicy: 'network-only'
  });
  const { data: accountData } = useQuery(GET_ACCOUNT, {
    fetchPolicy: 'network-only'
  });

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageDetailsVisible, setMessageDetailsVisible] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [campaignCustomersCount, setCampaignCustomersCount] = useState(0);

  const steps = [
    {
      title: 'Informações Básicas',
      content: 'basic-info',
    },
    {
      title: 'Seleção de Itens',
      content: 'select-items',
    },
    {
      title: 'Definir Mensagem',
      content: 'define-details',
    },
    {
      title: 'Revisar',
      content: 'review',
    },
    {
      title: 'Mensagens',
      content: 'messages',
    },
    {
      title: 'Enviar',
      content: 'send',
    }
  ];

  // Step handlers
  const handleStep0 = async () => {
    await form.validateFields(['name', 'kind']);
    const kind = form.getFieldValue('kind');
    if (kind === 'offer' || kind === 'follow_up') {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(currentStep + 2);
      if (generatedMessages.length === 0) {
        handleGenerateTemplate();
      }
    }
  };

  const handleStep1 = async () => {
    const kind = form.getFieldValue('kind');
    if (kind === 'follow_up') {
      if (customerSelectionMode === 'direct') {
        await form.validateFields(['followUpObjective', 'selectedCustomers']);
      } else if (customerSelectionMode === 'tags') {
        await form.validateFields(['followUpObjective', 'selectedTags']);
      } else if (customerSelectionMode === 'date_filters') {
        await form.validateFields(['followUpObjective', 'selectedDateFilters']);
      } else if (customerSelectionMode === 'advanced_filters') {
        await form.validateFields(['followUpObjective', 'selectedAdvancedFilters']);
      }
    } else {
      if (campaignMode === 'products') {
        if (customerSelectionMode === 'direct') {
          await form.validateFields(['selectedProducts', 'selectedCustomers', 'discountPercentage']);
        } else if (customerSelectionMode === 'tags') {
          await form.validateFields(['selectedProducts', 'selectedTags', 'discountPercentage']);
        } else if (customerSelectionMode === 'date_filters') {
          await form.validateFields(['selectedProducts', 'selectedDateFilters', 'discountPercentage']);
        } else if (customerSelectionMode === 'advanced_filters') {
          await form.validateFields(['selectedProducts', 'selectedAdvancedFilters', 'discountPercentage']);
        }
      } else {
        if (customerSelectionMode === 'direct') {
          await form.validateFields(['selectedGroup', 'selectedCustomers', 'discountPercentage']);
        } else if (customerSelectionMode === 'tags') {
          await form.validateFields(['selectedGroup', 'selectedTags', 'discountPercentage']);
        } else if (customerSelectionMode === 'date_filters') {
          await form.validateFields(['selectedGroup', 'selectedDateFilters', 'discountPercentage']);
        } else if (customerSelectionMode === 'advanced_filters') {
          await form.validateFields(['selectedGroup', 'selectedAdvancedFilters', 'discountPercentage']);
        }
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handleStep2 = async () => {
    if (generatedMessages.length === 0) {
      setError("Por favor, gere algumas mensagens de prévia antes de prosseguir.");
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const handleStep3 = async () => {
    if (previewMode) {
      const values = form.getFieldsValue([
        'name',
        'kind',
        'aiPromptTemplate',
        'selectedProducts',
        'selectedCustomers',
        'selectedGroup',
        'discountPercentage',
        'customerSelectionMode',
        'selectedTags',
        'selectedDateFilters',
        'selectedAdvancedFilters',
        'followUpObjective',
        'fileList'
      ]);
      const fileList = values.fileList || [];
      const images = fileList.filter(file => file && file.originFileObj).map(file => file.originFileObj);
      const { data } = await createCampaign({
        variables: {
          input: {
            name: values.name,
            kind: values.kind,
            promptTemplate: values.aiPromptTemplate,
            selectedProductIds: campaignMode === 'products' ? values.selectedProducts : [],
            selectedCustomerIds: customerSelectionMode === 'direct' ? values.selectedCustomers : [],
            selectedGroupId: campaignMode === 'group' ? values.selectedGroup : null,
            discountPercentage: values.discountPercentage ? parseFloat(values.discountPercentage) || 0 : 0,
            selectedTagIds: customerSelectionMode === 'tags' ? values.selectedTags : [],
            selectedDateFilters: customerSelectionMode === 'date_filters' ? values.selectedDateFilters : [],
            selectedAdvancedFilters: customerSelectionMode === 'advanced_filters' ? values.selectedAdvancedFilters : [],
            followUpObjective: values.followUpObjective || null,
            images: images.length > 0 ? images : null,
            expectedCustomersCount: campaignCustomersCount || 0,
            onlyVerifiedWhatsappCampaigns: account?.onlyVerifiedWhatsappCampaigns || false
          }
        }
      });
      if (data.createCampaign.errors && data.createCampaign.errors.length > 0) {
        setError(data.createCampaign.errors.join(', '));
        return;
      }
      setCampaignId(data.createCampaign.campaign.id);
      setPreviewMode(false);
      const processParams = {
        id: data.createCampaign.campaign.id,
        promptTemplate: values.aiPromptTemplate || null,
        scheduledFor: null,
      };
      if ((values.kind === 'offer' || values.kind === 'follow_up') && customerSelectionMode === 'direct' && values.selectedCustomers?.length) {
        processParams.customerIds = values.selectedCustomers;
      }
      if ((values.kind === 'offer' || values.kind === 'follow_up') && customerSelectionMode === 'tags' && values.selectedTags?.length) {
        processParams.tagIds = values.selectedTags;
      }
      if ((values.kind === 'offer' || values.kind === 'follow_up') && customerSelectionMode === 'date_filters' && values.selectedDateFilters?.length) {
        processParams.dateFilters = values.selectedDateFilters;
      }
      if ((values.kind === 'offer' || values.kind === 'follow_up') && customerSelectionMode === 'advanced_filters' && values.selectedAdvancedFilters?.length) {
        processParams.advancedFilters = values.selectedAdvancedFilters;
      }
      console.log('Processando campanha com parâmetros:', processParams);
      const processResult = await processCampaign({
        variables: processParams
      });
      if (processResult.data.processCampaign.errors &&
        processResult.data.processCampaign.errors.length > 0) {
        setError(processResult.data.processCampaign.errors.join(', '));
        return;
      }
      setTotalMessagesCount(processResult.data.processCampaign.campaignMessagesCount);
      setCampaignProcessed(true);
      setMessageStatus("pending");
      setCurrentStep(currentStep + 1);
      loadCampaignMessages();
    } else {
      setCurrentStep(currentStep + 1);
      if (campaignId && !campaignMessages.length) {
        loadCampaignMessages();
      }
    }
  };

  const handleStep4 = async () => {
    setCurrentStep(currentStep + 1);
  };

  const handleStep5 = async () => {
    await form.validateFields(['scheduledFor']);
    const values = form.getFieldsValue(['scheduledFor']);
    if (values.scheduledFor && values.scheduledFor.isBefore(dayjs().subtract(5, 'minute'))) {
      setError('A data de agendamento não pode ser anterior ao horário atual.');
      return;
    }
    const { data } = await scheduleCampaign({
      variables: {
        id: campaignId,
        scheduledFor: values.scheduledFor || null
      }
    });
    if (data.scheduleCampaign.errors && data.scheduleCampaign.errors.length > 0) {
      setError(data.scheduleCampaign.errors.join(', '));
      return;
    }
    onFinish({
      id: campaignId,
      messagesCount: data.scheduleCampaign.campaignMessagesCount || totalMessagesCount
    });
  };

  const handleNext = async () => {
    try {
      if (isGeneratingTemplate) {
        return;
      }
      if (currentStep === 0) {
        await handleStep0();
      } else if (currentStep === 1) {
        await handleStep1();
      } else if (currentStep === 2) {
        await handleStep2();
      } else if (currentStep === 3) {
        await handleStep3();
      } else if (currentStep === 4) {
        await handleStep4();
      } else if (currentStep === 5) {
        await handleStep5();
      }
    } catch (error) {
      console.error(error);
      setError('Ocorreu um erro ao processar a campanha.');
    }
  };

  const handlePrev = () => {
    // Prevent navigation during preview generation
    if (isGeneratingTemplate) {
      return;
    }

    const kind = form.getFieldValue('kind');

    if (currentStep === 2 && kind !== 'offer' && kind !== 'follow_up') {
      // Se estiver na definição de mensagem e não for oferta nem follow_up, volta para o início
      setCurrentStep(0);
    } else if (currentStep === 1) {
      setCurrentStep(currentStep - 1);
    } else if (currentStep === 4 && campaignProcessed) {
      // Se já processamos a campanha e estamos voltando do passo 4 para o 3,
      // manter o estado para evitar reprocessamento
      setCurrentStep(currentStep - 1);
    } else {
      // Apenas alteramos o passo sem modificar o estado previewMode
      // para evitar reprocessamento desnecessário
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGenerateTemplate = async () => {
    // Prevent multiple simultaneous generation requests
    if (isGeneratingTemplate) {
      return;
    }

    setIsGeneratingTemplate(true);
    setGeneratedMessages([]);
    setError(null);

    try {
      const kind = form.getFieldValue('kind') || 'offer';

      // Additional params for offer type
      const params = { kind };

      // Get the custom AI prompt if available
      const aiPrompt = form.getFieldValue('aiPromptTemplate');
      if (aiPrompt && aiPrompt.trim() !== '') {
        params.promptTemplate = aiPrompt;
      }

      // For offers, include selected products and customers
      if (kind === 'offer') {
        // Dependendo do modo de seleção, usar produtos ou grupo
        if (campaignMode === 'products') {
          const selectedProducts = form.getFieldValue('selectedProducts') || [];
          if (selectedProducts.length > 0) {
            params.productIds = selectedProducts;
          }
        } else if (campaignMode === 'group') {
          // Usar selectedGroupId caso o modo seja grupo
          const selectedGroup = form.getFieldValue('selectedGroup');
          if (selectedGroup) {
            console.log('Usando grupo selecionado para gerar prévia:', selectedGroup);
            params.selectedGroupId = selectedGroup;
          }
        }

        // Dependendo do modo de seleção de clientes, enviar clientes diretos, tags ou filtros de data
        if (customerSelectionMode === 'direct') {
          const selectedCustomers = form.getFieldValue('selectedCustomers') || [];
          if (selectedCustomers.length > 0) {
            params.customerIds = selectedCustomers;
          }
        } else if (customerSelectionMode === 'tags') {
          const selectedTags = form.getFieldValue('selectedTags') || [];
          if (selectedTags.length > 0) {
            params.tagIds = selectedTags;
          }
        } else if (customerSelectionMode === 'date_filters') {
          const selectedDateFilters = form.getFieldValue('selectedDateFilters') || [];
          if (selectedDateFilters.length > 0) {
            params.dateFilters = selectedDateFilters;
          }
        } else if (customerSelectionMode === 'advanced_filters') {
          const selectedAdvancedFilters = form.getFieldValue('selectedAdvancedFilters') || [];
          if (selectedAdvancedFilters.length > 0) {
            params.advancedFilters = selectedAdvancedFilters;
          }
        }

        const discountPercentage = form.getFieldValue('discountPercentage');

        // Only include discount percentage if it's greater than 0
        if (discountPercentage > 0) {
          params.discountPercentage = parseFloat(discountPercentage) || 0;
          console.log('Valor do params.discountPercentage após parseFloat:', params.discountPercentage, 'Tipo:', typeof params.discountPercentage);
        }
      }

      // For follow-up campaigns, include the objective and customers/tags
      if (kind === 'follow_up') {
        const followUpObjective = form.getFieldValue('followUpObjective');
        if (followUpObjective && followUpObjective.trim() !== '') {
          params.followUpObjective = followUpObjective.trim();
        }
        
        // Dependendo do modo de seleção de clientes, enviar clientes diretos, tags ou filtros de data
        if (customerSelectionMode === 'direct') {
          const selectedCustomers = form.getFieldValue('selectedCustomers') || [];
          if (selectedCustomers.length > 0) {
            params.customerIds = selectedCustomers;
          }
        } else if (customerSelectionMode === 'tags') {
          const selectedTags = form.getFieldValue('selectedTags') || [];
          if (selectedTags.length > 0) {
            params.tagIds = selectedTags;
          }
        } else if (customerSelectionMode === 'date_filters') {
          const selectedDateFilters = form.getFieldValue('selectedDateFilters') || [];
          if (selectedDateFilters.length > 0) {
            params.dateFilters = selectedDateFilters;
          }
        } else if (customerSelectionMode === 'advanced_filters') {
          const selectedAdvancedFilters = form.getFieldValue('selectedAdvancedFilters') || [];
          if (selectedAdvancedFilters.length > 0) {
            params.advancedFilters = selectedAdvancedFilters;
          }
        }
      }

      console.log('Enviando parâmetros para generatePreviewMessages:', params);
      // First, get personalized preview messages
      const previewResponse = await generatePreviewMessages({
        variables: params
      });

      if (previewResponse.data.generatePreviewMessages.errors &&
        previewResponse.data.generatePreviewMessages.errors.length > 0) {
        console.warn('Error generating preview messages:', previewResponse.data.generatePreviewMessages.errors);

        // Fall back to static previews if there's an error
        createStaticPreviews(kind);
        return;
      }

      // Use real customer preview messages from first API call
      setGeneratedMessages(
        previewResponse.data.generatePreviewMessages.messages.map((msg, index) => ({
          id: index + 1,
          customerName: msg.customerName,
          lastPurchase: msg.lastPurchase,
          favoriteProduct: msg.favoriteProduct,
          message: msg.message
        }))
      );
    } catch (error) {
      console.error('Error in template generation:', error);
      // Fallback to static templates if API fails
      const kind = form.getFieldValue('kind') || 'offer';
      createStaticPreviews(kind);

      // Show error message if it's likely due to missing information
      if (kind === 'offer') {
        let errorMessage;
        if (customerSelectionMode === 'direct') {
          errorMessage = "Não foi possível gerar mensagens. Verifique se você selecionou produtos e clientes.";
        } else if (customerSelectionMode === 'tags') {
          errorMessage = "Não foi possível gerar mensagens. Verifique se você selecionou produtos e tags.";
        } else if (customerSelectionMode === 'date_filters') {
          errorMessage = "Não foi possível gerar mensagens. Verifique se você selecionou produtos e aplicou filtros de data.";
        }
        setError(errorMessage);
      }
    } finally {
      setIsGeneratingTemplate(false);
    }
  };

  // Helper function for creating static preview examples as fallback
  const createStaticPreviews = (kind) => {

    // Create fully personalized sample messages (not using variables)
    const personalizedMessages = {
      offer: [
        {
          customerName: 'João Silva',
          lastPurchase: '15/02/2023',
          favoriteProduct: 'Cafeteira Elétrica',
          message: "Olá João, temos uma oferta especial para você! Confira nossos produtos em destaque. Entre em contato para aproveitar esta oportunidade exclusiva!"
        },
        {
          customerName: 'Maria Souza',
          lastPurchase: '20/03/2023',
          favoriteProduct: 'Liquidificador Multi Velocidades',
          message: "Olá Maria, preparamos uma oferta especial! Você vai adorar nossos produtos com condições especiais. Entre em contato hoje mesmo para aproveitar!"
        }
      ],
      reactivation: [
        {
          customerName: 'João Silva',
          lastPurchase: '15/02/2023',
          favoriteProduct: 'Cafeteira Elétrica',
          message: "Olá João, sentimos sua falta! Faz tempo que não recebemos um pedido seu desde fevereiro. Que tal fazer um novo pedido? Estamos à disposição!"
        },
        {
          customerName: 'Maria Souza',
          lastPurchase: '20/03/2023',
          favoriteProduct: 'Liquidificador Multi Velocidades',
          message: "Maria, estamos com saudades! Seu último pedido foi em março e gostaríamos de atendê-la novamente. Vamos conversar?"
        }
      ],
      activation: [
        {
          customerName: 'João Silva',
          lastPurchase: 'N/A',
          favoriteProduct: 'N/A',
          message: "Olá João! Seja bem-vindo à nossa loja. Estamos felizes em tê-lo como cliente. Agora você pode fazer pedidos pelo WhatsApp de forma rápida e fácil. Experimente fazer seu primeiro pedido hoje mesmo!"
        },
        {
          customerName: 'Maria Souza',
          lastPurchase: 'N/A',
          favoriteProduct: 'N/A',
          message: "Maria, que prazer tê-la como cliente! Agora você tem acesso a todos os nossos produtos diretamente pelo WhatsApp. Estamos ansiosos para atender seu primeiro pedido. Podemos ajudar em algo específico?"
        }
      ],
      follow_up: [
        {
          customerName: 'João Silva',
          lastPurchase: '15/02/2023',
          favoriteProduct: 'N/A',
          message: "Olá João! Entrando em contato para acompanhar nossa conversa anterior. Como podemos ajudá-lo hoje?"
        },
        {
          customerName: 'Maria Souza',
          lastPurchase: '20/03/2023',
          favoriteProduct: 'N/A',
          message: "Oi Maria! Seguindo nossa conversa, gostaria de verificar se tem alguma dúvida ou se posso ajudar em algo mais. Estou à disposição!"
        }
      ]
    };

    // Set generated messages from fallback data
    setGeneratedMessages(personalizedMessages[kind].map((msg, index) => ({
      id: index + 1,
      ...msg
    })));
  };


  const renderContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInfoStep
            campaignProcessed={campaignProcessed}
            selectedCampaignType={selectedCampaignType}
            onCampaignTypeChange={setSelectedCampaignType}
            prefetchData={prefetchOfferData}
          />
        );
      case 1:
        return (
          <SelectionStep
            campaignProcessed={campaignProcessed}
            campaignMode={campaignMode}
            onSelectionModeChange={handleSelectionModeChange}
            products={products}
            customers={customers}
            groups={groups}
            tags={tags}
            productsLoading={productsLoading}
            customersLoading={customersLoading}
            loadingGroups={loadingGroups}
            loadingTags={loadingTags}
            handleSearchGroups={handleSearchGroups}
            handleSearchTags={handleSearchTags}
            customerSelectionMode={customerSelectionMode}
            onCustomerSelectionModeChange={handleCustomerSelectionModeChange}
            selectedCampaignType={selectedCampaignType}
            onDateFiltersChange={handleDateFiltersChange}
            onAdvancedFiltersChange={handleAdvancedFiltersChange}
            getCampaignCustomersCount={getCampaignCustomersCount}
          />
        );
      case 2:
        return (
          <MessageStep
            isGeneratingTemplate={isGeneratingTemplate}
            handleGenerateTemplate={handleGenerateTemplate}
            campaignProcessed={campaignProcessed}
            showAiPrompt={showAiPrompt}
            setShowAiPrompt={setShowAiPrompt}
            generatedMessages={generatedMessages}
            form={form}
            previewMode={previewMode}
          />
        );
      case 3:
        return (
          <ReviewStep
            form={form}
            products={products}
            groups={groups}
            tags={tags}
            generatedMessages={generatedMessages}
            campaignMode={campaignMode}
            customerSelectionMode={customerSelectionMode}
            campaignCustomersCount={campaignCustomersCount}
            account={accountData?.account}
          />
        );
      case 4:
        return (
          <MessagesStep
            messageStatus={messageStatus}
            handleFilterMessages={handleFilterMessages}
            messagesLoading={messagesLoading}
            totalMessagesCount={totalMessagesCount}
            campaignMessages={campaignMessages}
            handlePageChange={handlePageChange}
            messagesPage={messagesPage}
            messagesPerPage={messagesPerPage}
            selectedMessage={selectedMessage}
            messageDetailsVisible={messageDetailsVisible}
            handleViewMessageDetails={handleViewMessageDetails}
            handleCloseMessageDetails={handleCloseMessageDetails}
            onMessageUpdated={handleMessageUpdated}
            onMessageDeleted={loadCampaignMessages}
          />
        );
      case 5:
        return (
          <SendStep
            totalMessagesCount={totalMessagesCount}
            form={form}
          />
        );
      default:
        return null;
    }
  };

  const formInitialValues = {
    name: '',
    kind: 'offer',
    scheduledFor: null,
    selectedProducts: [],
    selectedCustomers: [],
    selectedTags: [],
    selectedDateFilters: [],
    selectedAdvancedFilters: [],
    aiPromptTemplate: ''
  };

  useEffect(() => {
    if (!initialCampaignState?.campaignId) {
      return;
    }

    form.setFieldsValue({
      name: initialCampaignState.name || '',
      kind: initialCampaignState.kind || 'offer',
      scheduledFor: null
    });

    setCampaignId(initialCampaignState.campaignId);
    setSelectedCampaignType(initialCampaignState.kind || 'offer');
    setSelectionMode(initialCampaignState.campaignMode || 'products');
    setCampaignCustomersCount(initialCampaignState.expectedCustomersCount || 0);
    setTotalMessagesCount(initialCampaignState.totalMessagesCount || 0);
    setPreviewMode(false);
    setCampaignProcessed(true);
    setMessageStatus('pending');
    setCurrentStep(4);
    setError(null);
  }, [form, initialCampaignState]);

  // Add useQuery for products and customers with skip to control when they load
  const { loading: productsLoading } = useQuery(GET_PRODUCTS, {
    skip: form?.getFieldValue('kind') !== 'offer',
    onCompleted: (data) => {
      if (data && data.products) {
        setProducts(data.products.edges.map(edge => ({
          key: edge.node.id,
          id: edge.node.id,
          name: edge.node.name,
          price: edge.node.price,
          active: edge.node.active
        })));
      }
    }
  });

  const { loading: customersLoading } = useQuery(GET_CUSTOMERS, {
    skip: form?.getFieldValue('kind') !== 'offer',
    variables: {
      withWhatsapp: true  // Para campanhas, só carregar clientes com WhatsApp
    },
    onCompleted: (data) => {
      if (data && data.customers) {
        setCustomers(data.customers.edges.map(edge => ({
          key: edge.node.id,
          id: edge.node.id,
          name: edge.node.name,
          document: edge.node.document,
          whatsapp: edge.node.whatsapp
        })));
      }
    }
  });

  // Load products and customers when user selects "offer" kind
  useEffect(() => {
    if (initialCampaignState?.campaignId) {
      return;
    }

    const kind = form.getFieldValue('kind');
    if (kind === 'offer' && currentStep === 0) {
      // Prefetch products and customers for next step
      client.query({ query: GET_PRODUCTS })
        .then(({ data }) => {
          if (data && data.products) {
            setProducts(data.products.edges.map(edge => ({
              key: edge.node.id,
              id: edge.node.id,
              name: edge.node.name,
              price: edge.node.price,
              active: edge.node.active
            })));
          }
        })
        .catch(err => console.error('Error loading products:', err));

      client.query({ 
        query: GET_CUSTOMERS,
        variables: { withWhatsapp: true }  // Para campanhas, só carregar clientes com WhatsApp
      })
        .then(({ data }) => {
          if (data && data.customers) {
            setCustomers(data.customers.edges.map(edge => ({
              key: edge.node.id,
              id: edge.node.id,
              name: edge.node.name,
              document: edge.node.document,
              whatsapp: edge.node.whatsapp
            })));
          }
        })
        .catch(err => console.error('Error loading customers:', err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, form, currentStep, initialCampaignState]);

  // Add a new effect to check for empty message template when step changes
  useEffect(() => {
    if (initialCampaignState?.campaignId) {
      return;
    }

    if (currentStep === 2) {
      if (generatedMessages.length === 0) {
        handleGenerateTemplate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, initialCampaignState]);

  // Add effect to load messages when campaignId changes or when entering the messages step
  useEffect(() => {
    if (campaignId && currentStep === 4) {
      // Avoid triggering when changing messageStatus as handleFilterMessages already handles that
      loadCampaignMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, currentStep]);

  // Initialize the campaign type from form values
  useEffect(() => {
    if (initialCampaignState?.campaignId) {
      return;
    }

    const initialKind = form.getFieldValue('kind');
    if (initialKind) {
      setSelectedCampaignType(initialKind);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, initialCampaignState]);

  // Carregar grupos quando o componente for montado
  useEffect(() => {
    // Carregar grupos para ter disponível caso o usuário selecione o modo grupo
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Monitorar mudanças no grupo selecionado
  useEffect(() => {
    const selectedGroupId = form.getFieldValue('selectedGroup');
    if (selectedGroupId && campaignMode === 'group') {
      // Se já temos o grupo na lista, não precisamos buscar
      const existingGroup = groups.find(g => g.id === selectedGroupId);
      if (!existingGroup) {
        loadGroupDetails(selectedGroupId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue('selectedGroup'), campaignMode]);

  // Carregar tags quando o componente for montado
  useEffect(() => {
    // Carregar tags para ter disponível caso o usuário selecione o modo por tags
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load campaign customers count when campaign type or selections change
  useEffect(() => {
    if (currentStep === 3) { // Only on review step
      loadCampaignCustomersCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, form.getFieldValue('kind'), form.getFieldValue('selectedCustomers'), form.getFieldValue('selectedTags'), customerSelectionMode]);

  // Prefetch data when user selects "offer" type
  const prefetchOfferData = () => {
          client.query({ query: GET_PRODUCTS });
      client.query({ 
        query: GET_CUSTOMERS, 
        variables: { withWhatsapp: true }  // Para campanhas, só carregar clientes com WhatsApp
      });
      client.query({ query: SEARCH_TAGS, variables: { query: "*" } });
  };

  const loadCampaignMessages = async () => {
    if (!campaignId) return;

    setMessagesLoading(true);

    try {
      // Usar o status atualmente selecionado (pendentes por padrão)
      const currentStatus = messageStatus || "pending";

      // Carregar mensagens da campanha usando a query GET_CAMPAIGN_MESSAGES
      const { data } = await client.query({
        query: GET_CAMPAIGN_MESSAGES,
        variables: {
          campaignId: campaignId,
          page: messagesPage,
          perPage: messagesPerPage,
          status: currentStatus
        },
        fetchPolicy: 'network-only'
      });

      if (data && data.campaignMessages) {
        const messages = data.campaignMessages.edges.map(({ node }) => ({
          ...node,
          key: node.id
        }));

        setCampaignMessages(messages);
        setTotalMessagesCount(data.campaignMessages.totalCount);
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      setError('Erro ao carregar mensagens da campanha.');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setMessagesPage(page);
    setMessagesPerPage(pageSize);
    loadCampaignMessages();
  };

  const handleFilterMessages = (status) => {
    // Force immediate re-render to update UI buttons
    setMessagesLoading(true);

    // Set the new status and reset to page 1
    setMessageStatus(status);
    setMessagesPage(1);

    // Load the messages with the new status filter
    // Using setTimeout to ensure state is updated before loading
    setTimeout(() => {
      // Explicitly load with the new status to avoid race conditions
      loadCampaignMessagesWithStatus(status);
    }, 0);
  };

  // Helper function to load messages with a specific status
  const loadCampaignMessagesWithStatus = async (status) => {
    if (!campaignId) return;

    try {
      // Use the provided status directly to avoid race conditions with state updates
      const { data } = await client.query({
        query: GET_CAMPAIGN_MESSAGES,
        variables: {
          campaignId: campaignId,
          page: messagesPage,
          perPage: messagesPerPage,
          status: status
        },
        fetchPolicy: 'network-only'
      });

      if (data && data.campaignMessages) {
        const messages = data.campaignMessages.edges.map(({ node }) => ({
          ...node,
          key: node.id
        }));

        setCampaignMessages(messages);
        setTotalMessagesCount(data.campaignMessages.totalCount);
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      setError('Erro ao carregar mensagens da campanha.');
    } finally {
      setMessagesLoading(false);
    }
  };

  // View message details handler
  const handleViewMessageDetails = (message) => {
    setSelectedMessage(message);
    setMessageDetailsVisible(true);
  };

  const handleCloseMessageDetails = () => {
    setMessageDetailsVisible(false);
    setSelectedMessage(null);
  };

  // Função para carregar todos os grupos ativos
  const loadGroups = useCallback(() => {
    setLoadingGroups(true);
    // Usar wildcard "*" para buscar todos os grupos ativos
    searchGroups({
      variables: { query: "*" },
      onCompleted: (data) => {
        if (data && data.searchGroups) {
          console.log("Grupos carregados:", data.searchGroups);
          setGroups(data.searchGroups);
        }
        setLoadingGroups(false);
      },
      onError: (error) => {
        console.error('Erro ao carregar grupos:', error);
        setLoadingGroups(false);
      }
    });
  }, [searchGroups]);

  // Função para pesquisar grupos quando o usuário digitar com debounce
  const debouncedSearchGroups = useCallback((value) => {
    // Limpa o timeout anterior se existir
    if (groupsTimeoutRef.current) {
      clearTimeout(groupsTimeoutRef.current);
    }
    
    groupsTimeoutRef.current = setTimeout(() => {
      if (!value) {
        // Se a busca for vazia, carrega todos os grupos
        loadGroups();
        return;
      }

      if (value.length >= 2) {
        setLoadingGroups(true);
        searchGroups({
          variables: { query: value },
          onCompleted: (data) => {
            if (data && data.searchGroups) {
              console.log("Grupos encontrados:", data.searchGroups);
              setGroups(data.searchGroups);
            }
            setLoadingGroups(false);
          },
          onError: (error) => {
            console.error('Erro ao buscar grupos:', error);
            setLoadingGroups(false);
          }
        });
      }
    }, 1000); // 1000ms de delay
  }, [searchGroups, loadGroups]);

  const handleSearchGroups = (value) => {
    debouncedSearchGroups(value);
  };

  const handleSelectionModeChange = (e) => {
    const mode = e.target.value;
    setSelectionMode(mode);

    // Limpar o campo oposto quando mudar o modo
    if (mode === 'products') {
      form.setFieldsValue({ selectedGroup: undefined });
    } else {
      form.setFieldsValue({ selectedProducts: [] });

      // Carregar os grupos quando este modo for selecionado
      loadGroups();
    }
  };

  // Função para buscar detalhes de um grupo específico
  const loadGroupDetails = async (groupId) => {
    if (!groupId) return null;

    try {
      const { data } = await client.query({
        query: GET_GROUP,
        variables: { id: groupId }
      });

      if (data && data.group) {
        console.log('Detalhes do grupo carregados:', data.group);
        // Atualizar o grupo na lista caso ele não exista ou esteja desatualizado
        const existingGroupIndex = groups.findIndex(g => g.id === data.group.id);
        if (existingGroupIndex === -1) {
          setGroups([...groups, data.group]);
        } else if (JSON.stringify(groups[existingGroupIndex]) !== JSON.stringify(data.group)) {
          const updatedGroups = [...groups];
          updatedGroups[existingGroupIndex] = data.group;
          setGroups(updatedGroups);
        }
        return data.group;
      }
      return null;
    } catch (error) {
      console.error('Erro ao carregar detalhes do grupo:', error);
      return null;
    }
  };

  const handleMessageUpdated = (updatedMessage) => {
    // Update the message in the local state
    const updatedMessages = campaignMessages.map(msg => {
      if (msg.id === updatedMessage.id) {
        return {
          ...msg,
          message: updatedMessage.message,
          updatedAt: updatedMessage.updatedAt
        };
      }
      return msg;
    });

    setCampaignMessages(updatedMessages);
    setSelectedMessage(updatedMessage);

    // Reload campaign messages to get fresh data
    loadCampaignMessages();
  };

  // Função para carregar todas as tags ativas
  const loadTags = useCallback(() => {
    setLoadingTags(true);
    // Buscar todas as tags ativas usando o wildcard "*"
    client.query({
      query: SEARCH_TAGS,
      variables: { query: "*" },
      fetchPolicy: 'network-only'
    })
      .then(({ data }) => {
        if (data && data.searchTags) {
          console.log("Tags carregadas:", data.searchTags);
          setTags(data.searchTags);
        }
        setLoadingTags(false);
      })
      .catch(error => {
        console.error('Erro ao carregar tags:', error);
        setLoadingTags(false);
      });
  }, [client]);

  // Função para pesquisar tags quando o usuário digitar com debounce
  const debouncedSearchTags = useCallback((value) => {
    // Limpa o timeout anterior se existir
    if (tagsTimeoutRef.current) {
      clearTimeout(tagsTimeoutRef.current);
    }
    
    tagsTimeoutRef.current = setTimeout(() => {
      if (!value) {
        // Se a busca for vazia, carrega todas as tags
        loadTags();
        return;
      }

      if (value.length >= 2) {
        setLoadingTags(true);
        searchTags({
          variables: { query: value },
          onCompleted: (data) => {
            if (data && data.searchTags) {
              console.log("Tags encontradas:", data.searchTags);
              setTags(data.searchTags);
            }
            setLoadingTags(false);
          },
          onError: (error) => {
            console.error('Erro ao buscar tags:', error);
            setLoadingTags(false);
          }
        });
      }
          }, 1000); // 1000ms de delay
    }, [searchTags, loadTags]);

  const handleSearchTags = (value) => {
    debouncedSearchTags(value);
  };

  const handleCustomerSelectionModeChange = (mode) => {
    setCustomerSelectionMode(mode);

    // Limpar os campos dos outros modos quando mudar o modo
    if (mode === 'direct') {
      form.setFieldsValue({ selectedTags: undefined, selectedDateFilters: [] });
    } else if (mode === 'tags') {
      form.setFieldsValue({ selectedCustomers: [], selectedDateFilters: [] });
      // Carregar as tags quando este modo for selecionado
      loadTags();
    } else if (mode === 'date_filters') {
      form.setFieldsValue({ selectedCustomers: [], selectedTags: undefined });
    }
  };

  // Função para lidar com mudanças nos filtros de data
  const handleDateFiltersChange = (filters) => {
    form.setFieldsValue({ selectedDateFilters: filters });
  };

  // Função para lidar com mudanças nos filtros avançados
  const handleAdvancedFiltersChange = (filters) => {
    form.setFieldsValue({ selectedAdvancedFilters: filters });
  };

  // Load campaign customers count based on campaign type and selections
  const loadCampaignCustomersCount = useCallback(async () => {
    try {
      const kind = form.getFieldValue('kind');
      if (!kind) return;

      const params = { kind };

      // For offer and follow_up campaigns, include selected customers, tags, date filters or advanced filters
      if (kind === 'offer' || kind === 'follow_up') {
        if (customerSelectionMode === 'direct') {
          const selectedCustomers = form.getFieldValue('selectedCustomers') || [];
          if (selectedCustomers.length > 0) {
            params.customerIds = selectedCustomers;
          }
        } else if (customerSelectionMode === 'tags') {
          const selectedTags = form.getFieldValue('selectedTags') || [];
          if (selectedTags.length > 0) {
            params.tagIds = selectedTags;
          }
        } else if (customerSelectionMode === 'date_filters') {
          const selectedDateFilters = form.getFieldValue('selectedDateFilters') || [];
          if (selectedDateFilters.length > 0) {
            params.dateFilters = selectedDateFilters;
          }
        } else if (customerSelectionMode === 'advanced_filters') {
          const selectedAdvancedFilters = form.getFieldValue('selectedAdvancedFilters') || [];
          if (selectedAdvancedFilters.length > 0) {
            params.advancedFilters = selectedAdvancedFilters;
          }
        }
      }
      const { data } = await getCampaignCustomersCount({
        variables: params
      });

      if (data?.campaignCustomersCount !== undefined) {
        setCampaignCustomersCount(data.campaignCustomersCount);
      }
    } catch (error) {
      console.error('Error loading campaign customers count:', error);
    }
  }, [form, customerSelectionMode, getCampaignCustomersCount]);

  // Cleanup dos timeouts quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (groupsTimeoutRef.current) {
        clearTimeout(groupsTimeoutRef.current);
      }
      if (tagsTimeoutRef.current) {
        clearTimeout(tagsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Modal
        open={processingCampaign}
        closable={false}
        footer={null}
        centered
        width={500}
        maskClosable={false}
        keyboard={false}
        style={{ textAlign: 'center' }}
      >
        <div style={{ padding: '20px 0' }}>
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
            style={{ display: 'block', marginBottom: 24 }}
          />
          
          <Title level={3} style={{ marginBottom: 16, color: '#1890ff' }}>
            Processando Mensagens da Campanha
          </Title>
          
          <Text style={{ fontSize: 16, color: '#666', display: 'block', marginBottom: 16 }}>
            Estamos gerando as mensagens personalizadas para os clientes selecionados.
          </Text>
          
          <Text style={{ fontSize: 14, color: '#999', display: 'block', marginBottom: 20 }}>
            Este processo pode demorar alguns minutos dependendo da quantidade de clientes.
          </Text>
          
          <Progress 
            percent={100} 
            status="active" 
            showInfo={false}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
            style={{ marginBottom: 16 }}
          />
          
          <Text style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
            Por favor, aguarde e não feche esta janela...
          </Text>
        </div>
      </Modal>

      <Spin spinning={creatingCampaign}>
        <Form
          form={form}
          layout="vertical"
          initialValues={formInitialValues}
          onValuesChange={(changedValues) => {
            // If the campaign kind changes, update our state
            if (changedValues.kind) {
              setSelectedCampaignType(changedValues.kind);
            }
          }}
        >
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          {steps.map(item => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>

        {error && (
          <Alert
            message="Erro"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        {renderContent()}

        <div style={{ marginTop: 24, textAlign: 'right' }}>
          {currentStep > 0 && (
            <Button
              style={{ marginRight: 8 }}
              onClick={handlePrev}
              disabled={isGeneratingTemplate || processingCampaign}>
              Voltar
            </Button>
          )}

          <Button
            type="primary"
            onClick={handleNext}
            loading={creatingCampaign || processingCampaign || schedulingCampaign}
            disabled={isGeneratingTemplate || processingCampaign}
          >
            {currentStep === steps.length - 1
              ? 'Agendar e Enviar'
              : currentStep === 3
                ? (campaignProcessed ? 'Próximo' : 'Salvar e processar')
                : 'Próximo'
            }
          </Button>

          <Button style={{ marginLeft: 8 }} onClick={onCancel} disabled={processingCampaign}>
            Cancelar
          </Button>
        </div>
      </Form>
    </Spin>
    </>
  );
};

export default CampaignForm;